import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Row, Table, Col, Button } from "reactstrap";
import Loading from "components/Common/Loading";
import MultipleWithNoButtons from "components/widgets/multiple-with-no-buttons";
import organizationalStructureService from "services/OrganizationalStructureService";
import indicadoresService from "services/IndicadoresService";
import Select from "react-select";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FileXls } from "@phosphor-icons/react";
import { generateYearOptions } from "helpers/yearHelper";
import { formatIndicatorDisplayValue } from "helpers/indicatorValueHelper";
import { computeVariablesTotals } from "helpers/indicatorTotalsHelper";
import "./style.css";

const VisualizarIndicador = () => {
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [goalData, setGoalData] = useState();

	const [formattedData, setFormattedData] = useState({});
	const [totals, setTotals] = useState({});
	const [variableConfigs, setVariableConfigs] = useState([]);
	const [queryParameters] = useSearchParams();

	const id = queryParameters.get("id");
	const dataFilter = queryParameters.get("data");
	const navigate = useNavigate();

	const yearFromURL = queryParameters.get("year");
	const [year, setYear] = useState(
		yearFromURL ? parseInt(yearFromURL, 10) : new Date().getFullYear(),
	);

	// Inicializa indicatorIds como null
	const [indicatorIds, setIndicatorIds] = useState(null);
	const [currentIndex, setCurrentIndex] = useState(0);

	const chartRef = useRef(null);

	document.title = "Senda | Indicador";

	const isLegacyIndicator =
		(goalData?.structure_version ?? data?.structure_version ?? "legacy") !==
		"variables";

	const normalizeVariableConfig = (config) => ({
		id: config.id,
		name: config.name || config.GoalsVariable?.name || "",
		quantifier:
			config.quantifier || config.GoalsVariable?.quantifier || null,
		decimals_cases:
			config.GoalsVariable?.decimals_cases != null
				? Number(config.GoalsVariable.decimals_cases)
				: config.decimals_cases != null
					? Number(config.decimals_cases)
					: 2,
		sequence: Number(config.sequence || 0),
	});

	const getTableColumnEntries = () => {
		if (isLegacyIndicator) {
			const configs = Array.isArray(data?.goals_config)
				? data.goals_config
				: Object.values(data?.goals_config || {});

			return configs
				.sort(
					(a, b) => Number(a.sequence || 0) - Number(b.sequence || 0),
				)
				.map((col) => [String(col.id), col]);
		}

		return (variableConfigs || [])
			.sort(
				(a, b) => Number(a.sequence || 0) - Number(b.sequence || 0),
			)
			.map((col) => [String(col.id), normalizeVariableConfig(col)]);
	};

	const buildPeriods = (periodicity) => {
		const p = (periodicity || "").toLowerCase();
		switch (p) {
			case "bimestral":
				return [
					{ id: 1, label: "Jan-Fev", monthStart: 1 },
					{ id: 2, label: "Mar-Abr", monthStart: 3 },
					{ id: 3, label: "Mai-Jun", monthStart: 5 },
					{ id: 4, label: "Jul-Ago", monthStart: 7 },
					{ id: 5, label: "Set-Out", monthStart: 9 },
					{ id: 6, label: "Nov-Dez", monthStart: 11 },
				];
			case "trimestral":
				return [
					{ id: 1, label: "Jan-Mar", monthStart: 1 },
					{ id: 2, label: "Abr-Jun", monthStart: 4 },
					{ id: 3, label: "Jul-Set", monthStart: 7 },
					{ id: 4, label: "Out-Dez", monthStart: 10 },
				];
			case "quadrimestral":
				return [
					{ id: 1, label: "Jan-Abr", monthStart: 1 },
					{ id: 2, label: "Mai-Ago", monthStart: 5 },
					{ id: 3, label: "Set-Dez", monthStart: 9 },
				];
			case "semestral":
				return [
					{ id: 1, label: "Jan-Jun", monthStart: 1 },
					{ id: 2, label: "Jul-Dez", monthStart: 7 },
				];
			case "anual":
				return [{ id: 1, label: "Jan-Dez", monthStart: 1 }];
			case "mensal":
			default:
				return [
					{ id: 1, label: "Jan", monthStart: 1 },
					{ id: 2, label: "Fev", monthStart: 2 },
					{ id: 3, label: "Mar", monthStart: 3 },
					{ id: 4, label: "Abr", monthStart: 4 },
					{ id: 5, label: "Mai", monthStart: 5 },
					{ id: 6, label: "Jun", monthStart: 6 },
					{ id: 7, label: "Jul", monthStart: 7 },
					{ id: 8, label: "Ago", monthStart: 8 },
					{ id: 9, label: "Set", monthStart: 9 },
					{ id: 10, label: "Out", monthStart: 10 },
					{ id: 11, label: "Nov", monthStart: 11 },
					{ id: 12, label: "Dez", monthStart: 12 },
				];
		}
	};

	const getItemsColumns = async () => {
		try {
			const response = await indicadoresService.getById(id);
			setData(response);
			return response;
		} catch (error) {
			console.log(error);
			return null;
		}
	};

	const getItemsTable = async () => {
		try {
			const response = await indicadoresService.getLaunchWithTotals(
				id,
				year,
			);
			const { goalsConfig, totals } = response;

			let items = {};
			goalsConfig.forEach((item) =>
				item.goals_config_values.forEach((field) => {
					items = {
						...items,
						[item.id]: {
							...items[item.id],
							[field.month]: field.value,
						},
					};
				}),
			);

			setFormattedData(items);
			setTotals(totals);
		} catch (error) {
			console.log(error);
		}
	};

	const getIds = async () => {
		try {
			const ids = await organizationalStructureService.getIndicatorIds({
				dataFilter,
			});
			setIndicatorIds(ids);

			// Definir o índice atual com base no ID atual
			const currentIdIndex = ids.indexOf(id);
			setCurrentIndex(currentIdIndex !== -1 ? currentIdIndex : 0);
		} catch (error) {
			console.log(error);
		}
	};

	const reloadIds = async () => {
		if (dataFilter) {
			try {
				const ids = await organizationalStructureService.getIndicatorIds({
					dataFilter,
				});
				setIndicatorIds(ids);
				return ids;
			} catch (error) {
				console.log(error);
				return indicatorIds; // Retorna a lista atual em caso de erro
			}
		}
		return indicatorIds; // Retorna a lista atual se não há dataFilter
	};

	const getItems = async () => {
		try {
			setIsLoading(true);
			const [dashboardResponse, goalDetail] = await Promise.all([
				organizationalStructureService
					.getDashboardById({ year: year }, id)
					.then((response) => {
						setGoalData(response);
						return response;
					}),
				getItemsColumns(),
			]);

			const structureVersion =
				goalDetail?.structure_version ??
				dashboardResponse?.structure_version ??
				"legacy";

			if (structureVersion !== "variables") {
				await getItemsTable();
			} else {
				const configs = await indicadoresService.getVariablesConfig(id);
				setVariableConfigs(configs || []);
			}

			if (dataFilter) {
				await getIds();
			} else {
				// Caso não haja dataFilter, inicializa indicatorIds como array vazio
				setIndicatorIds([]);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (id) {
			getItems();
		}
	}, [id]);

	// Atualiza quando o ano é alterado
	useEffect(() => {
		if (id) {
			getItems();
		}
	}, [year]);

	// Indicadores novos: valores vêm do dashboard; colunas vêm de goals_variables_config (todas).
	// Indicadores legacy: tabela usa getLaunchWithTotals + goals_config (todas as colunas).
	useEffect(() => {
		if (!goalData || !goalData.dados) return;
		if (isLegacyIndicator) return;

		let items = {};

		goalData.dados.forEach((entry) => {
			const cfgId = entry.goals_config_id;
			const monthIdx = parseInt(entry.month, 10);
			const rawValue =
				typeof entry.value === "number"
					? entry.value
					: parseFloat(entry.value || "0");

			if (!items[cfgId]) {
				items[cfgId] = {};
			}
			items[cfgId][monthIdx] = rawValue;
		});

		setFormattedData(items);
		setTotals(computeVariablesTotals(variableConfigs || [], items));
	}, [goalData, isLegacyIndicator, variableConfigs]);

	// Atualizar o índice quando a lista de IDs muda
	useEffect(() => {
		if (id && indicatorIds && indicatorIds.length > 0) {
			const newIndex = indicatorIds.indexOf(id);
			if (newIndex !== -1) {
				setCurrentIndex(newIndex);
			}
		}
	}, [indicatorIds, id]);

	const formatValue = (value, quantifier, decimals_cases) =>
		formatIndicatorDisplayValue(value, quantifier, decimals_cases, isLegacyIndicator);

	const years = generateYearOptions();

	const navigateToIndicator = (index) => {
		if (index >= 0 && indicatorIds && index < indicatorIds.length) {
			const newId = indicatorIds[index];
			navigate(`/indicadores/visualizar?id=${newId}&data=${dataFilter}`);
		}
	};

	const handlePrev = async () => {
		if (!indicatorIds) return;
		
		// Recarregar IDs antes de navegar
		const updatedIds = await reloadIds();
		
		// Recalcular o índice atual com base na lista atualizada
		const currentIdIndex = updatedIds.indexOf(id);
		if (currentIdIndex !== -1) {
			const newIndex = currentIdIndex === 0 ? updatedIds.length - 1 : currentIdIndex - 1;
			setCurrentIndex(newIndex);
			navigateToIndicator(newIndex);
		}
	};

	const handleNext = async () => {
		if (!indicatorIds) return;
		
		// Recarregar IDs antes de navegar
		const updatedIds = await reloadIds();
		
		// Recalcular o índice atual com base na lista atualizada
		const currentIdIndex = updatedIds.indexOf(id);
		if (currentIdIndex !== -1) {
			const newIndex = currentIdIndex === updatedIds.length - 1 ? 0 : currentIdIndex + 1;
			setCurrentIndex(newIndex);
			navigateToIndicator(newIndex);
		}
	};

	const exportToExcel = async () => {
		try {
			const chartInstance = chartRef.current.getChart();

			if (!chartInstance) {
				throw new Error(
					"Por favor, aguarde o carregamento completo do gráfico.",
				);
			}

			const { imgURI } = await chartInstance.dataURI();

			const img = new Image();
			img.src = imgURI;

			await new Promise((resolve) => {
				img.onload = resolve;
			});

			const FIXED_HEIGHT = 200;

			let height = FIXED_HEIGHT;
			let width = (img.width * FIXED_HEIGHT) / img.height;

			const workbook = new ExcelJS.Workbook();
			const worksheet = workbook.addWorksheet("Indicador");

			const imageId = workbook.addImage({
				base64: imgURI,
				extension: "png",
			});

			worksheet.addImage(imageId, {
				tl: { col: 0, row: 0 },
				ext: {
					width: width,
					height: height,
				},
				editAs: "absolute",
			});

			const STANDARD_SPACING_ROWS = 10;
			for (let i = 0; i < STANDARD_SPACING_ROWS; i++) {
				worksheet.addRow([]);
			}

			const periods = buildPeriods(goalData?.periodicity);

			const headers = ["Período"];
			getTableColumnEntries().forEach(([, coluna]) => {
				headers.push(coluna.name);
			});

			const headerRow = worksheet.addRow(headers);
			headerRow.font = { bold: true };
			headerRow.eachCell((cell) => {
				cell.border = {
					top: { style: "thin" },
					left: { style: "thin" },
					bottom: { style: "thin" },
					right: { style: "thin" },
				};
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "FFD9D9D9" },
				};
			});

			periods.forEach((period) => {
				const rowData = [period.label];
				getTableColumnEntries().forEach(([, coluna]) => {
					const value =
						formattedData[coluna.id]?.[period.monthStart];
					rowData.push(
						formatValue(
							value,
							coluna.quantifier,
							coluna.decimals_cases,
						),
					);
				});

				const row = worksheet.addRow(rowData);
				row.eachCell((cell) => {
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: "thin" },
					};
				});
			});

			const totalRowData = ["Totalizador"];
			getTableColumnEntries().forEach(([, coluna]) => {
				totalRowData.push(
					totals[coluna.id] !== undefined
						? formatValue(
								totals[coluna.id],
								coluna.quantifier,
								coluna.decimals_cases,
						  )
						: "",
				);
			});

			const totalRow = worksheet.addRow(totalRowData);
			totalRow.font = { bold: true };
			totalRow.eachCell((cell) => {
				cell.border = {
					top: { style: "thin" },
					left: { style: "thin" },
					bottom: { style: "thin" },
					right: { style: "thin" },
				};
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "FFE6E6E6" },
				};
			});

			worksheet.columns = [
				{ width: 20 }, // Coluna do período
				...getTableColumnEntries().map(() => ({ width: 30 })),
			];

			const buffer = await workbook.xlsx.writeBuffer();
			saveAs(
				new Blob([buffer]),
				`${goalData.intention_goals}_${year}.xlsx`,
			);
		} catch (error) {
			console.error("Erro ao exportar para Excel:", error);
		}
	};

	return (
		<Loading isLoading={isLoading}>
			<div
				className="page-content"
				style={{
					minHeight: "100vh",
					display: "flex",
					flexDirection: "column",
					overflow: "auto",
					padding: "10px",
					marginTop: "70px",
				}}
			>
				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: "15px",
						borderRadius: "10px",
						minHeight: "calc(100vh - 90px)",
						display: "flex",
						flexDirection: "column",
						gap: "20px",
					}}
				>
					{/* Gráfico - Ocupando quase toda a altura da viewport */}
					<div style={{ height: "calc(100vh - 100px)", flexShrink: 0 }}>
						<Row className="h-100">
							<Col
								xs="1"
								className="p-0 d-flex justify-content-center align-items-center"
							>
								<i
									onClick={handlePrev}
									className="fa fa-arrow-left"
									style={{
										cursor: "pointer",
										fontSize: "clamp(14px, 1.5vh, 18px)",
										visibility:
											indicatorIds?.length > 1
												? "visible"
												: "hidden",
										pointerEvents:
											indicatorIds?.length > 1
												? "auto"
												: "none",
									}}
								/>
							</Col>
							<Col xs="10" className="p-0 h-100">
								{goalData && (
									<div
										style={{
											height: "100%",
											width: "100%",
										}}
									>
										<MultipleWithNoButtons
											ref={chartRef}
											goal={goalData}
											currentYear={year}
											onYearChange={setYear}
											onExportXLSX={exportToExcel}
											availableYears={years.map(y => parseInt(y.value))}
										/>
									</div>
								)}
							</Col>
							<Col
								xs="1"
								className="p-0 d-flex justify-content-center align-items-center"
							>
								<i
									onClick={handleNext}
									className="fa fa-arrow-right"
									style={{
										cursor: "pointer",
										fontSize: "clamp(14px, 1.5vh, 18px)",
										visibility:
											indicatorIds?.length > 1
												? "visible"
												: "hidden",
										pointerEvents:
											indicatorIds?.length > 1
												? "auto"
												: "none",
									}}
								/>
							</Col>
						</Row>
					</div>

					{/* Tabela - Espaço restante mínimo */}
					<div style={{ flex: 1, minHeight: "300px" }}>
						{getTableColumnEntries().length > 0 && (
							<Table
								size="sm"
								className="mb-0"
								style={{
									fontSize: "clamp(0.75rem, 1.2vh, 0.85rem)",
									tableLayout: "fixed",
									width: "100%",
									borderRadius: "8px",
									border: "1px solid #e7e7e7",
									borderCollapse: "separate",
									borderSpacing: 0,
								}}
							>
								<thead
									style={{
										position: "sticky",
										top: 0,
										background: "white",
										zIndex: 10,
									}}
								>
									<tr>
										<th style={{ width: "80px" }}>Período</th>
										{getTableColumnEntries().map(([id, coluna]) => (
											<th
												key={id}
												style={{
													textAlign: "right",
													padding:
														"clamp(2px, 0.5vh, 4px) clamp(4px, 1vh, 8px)",
													minWidth: "80px",
												}}
											>
												{coluna.name}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{buildPeriods(goalData?.periodicity).map((period, i) => (
										<tr key={i}>
											<td
												style={{
													padding:
														"clamp(2px, 0.5vh, 4px) clamp(4px, 1vh, 8px)",
												}}
											>
												{period.label}
											</td>
											{getTableColumnEntries().map(
												([, coluna]) => {
													const value =
														formattedData[
															coluna.id
														]?.[period.monthStart];
													return (
														<td
															key={coluna.id}
															style={{
																textAlign:
																	"right",
																padding:
																	"clamp(2px, 0.5vh, 4px) clamp(4px, 1vh, 8px)",
																whiteSpace:
																	"nowrap",
															}}
															data-quantifier={
																coluna.quantifier
															}
														>
															{formatValue(
																value,
																coluna.quantifier,
																coluna.decimals_cases,
															)}
														</td>
													);
												},
											)}
										</tr>
									))}
									<tr className="totalizador-row">
										<td
											style={{
												padding:
													"clamp(2px, 0.5vh, 4px) clamp(4px, 1vh, 8px)",
											}}
										>
											Totalizador
										</td>
										{getTableColumnEntries().map(([id, coluna]) => (
											<td
												key={id}
												style={{
													textAlign: "right",
													padding:
														"clamp(2px, 0.5vh, 4px) clamp(4px, 1vh, 8px)",
													whiteSpace: "nowrap",
												}}
												data-quantifier={
													coluna.quantifier
												}
											>
												{totals[coluna.id] !== undefined
													? formatValue(
															totals[coluna.id],
															coluna.quantifier,
															coluna.decimals_cases,
													  )
													: ""}
											</td>
										))}
									</tr>
								</tbody>
							</Table>
						)}
					</div>
				</div>
			</div>
		</Loading>
	);
};

export default withTranslation()(VisualizarIndicador);
